import { createTheme } from '@mui/material/styles';

// Premium Enterprise Booking System - Unified MUI Theme
const premiumTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#2563eb', // More vibrant blue
      light: '#60a5fa',
      dark: '#1d4ed8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#f97316', // More vibrant orange
      light: '#fb923c',
      dark: '#ea580c',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#06b6d4',
      light: '#22d3ee',
      dark: '#0891b2',
      contrastText: '#FFFFFF',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
    },
    text: {
      primary: '#f8fafc',
      secondary: '#94a3b8',
      disabled: '#64748b',
    },
    background: {
      default: '#0a0a0a',
      paper: '#111111',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontWeight: 800,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.04em',
    },
    h2: {
      fontWeight: 800,
      fontSize: '3rem',
      lineHeight: 1.1,
      letterSpacing: '-0.04em',
    },
    h3: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.03em',
    },
    h4: {
      fontWeight: 700,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    body1: {
      fontWeight: 400,
      lineHeight: 1.6,
      fontSize: '1rem',
    },
    body2: {
      fontWeight: 400,
      lineHeight: 1.6,
      fontSize: '0.875rem',
    },
    button: {
      fontWeight: 600,
      fontSize: '0.9375rem',
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 2px 8px rgba(255, 107, 53, 0.15)',
    '0 4px 15px rgba(29, 78, 216, 0.1)',
    '0 4px 16px rgba(255, 107, 53, 0.25)',
    '0 6px 20px rgba(59, 130, 246, 0.4)',
    '0 8px 25px rgba(29, 78, 216, 0.2)',
    '0 8px 32px rgba(255, 107, 53, 0.35)',
    '0 10px 40px rgba(29, 78, 216, 0.2)',
    '0 12px 30px rgba(59, 130, 246, 0.5)',
    '0 20px 40px rgba(29, 78, 216, 0.25)',
    '0 25px 60px rgba(29, 78, 216, 0.3)',
    '0 0 20px rgba(255, 140, 66, 0.4)',
    '0 0 30px rgba(59, 130, 246, 0.5)',
    '0 0 40px rgba(59, 130, 246, 0.3)',
    '0 0 60px rgba(59, 130, 246, 0.3)',
    '0 0 80px rgba(59, 130, 246, 0.15)',
    '0 0 90px rgba(59, 130, 246, 0.1)',
    'inset 0 1px 0 rgba(255, 255, 255, 0.3)',
    'inset 0 1px 0 rgba(255, 255, 255, 0.4)',
    'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
    'inset 0 1px 0 rgba(255, 255, 255, 0.8)',
    'inset 0 -1px 0 rgba(59, 130, 246, 0.1)',
    '0 0 0 1px rgba(255, 255, 255, 0.3)',
    '0 0 0 1px rgba(255, 255, 255, 0.4)',
    '0 0 0 4px rgba(255, 140, 66, 0.2)',
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a0a0a',
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          fontWeight: 400,
          lineHeight: 1.6,
          color: '#f8fafc',
        },
        code: {
          fontFamily: "'JetBrains Mono', 'Fira Code', source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
        },
        '::-webkit-scrollbar': {
          width: '8px',
        },
        '::-webkit-scrollbar-track': {
          background: 'rgba(255, 255, 255, 0.02)',
        },
        '::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.2)',
        },
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-10px)' },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontWeight: 600,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          textTransform: 'none',
        },
        containedPrimary: {
          background: '#2563eb',
          color: '#ffffff',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            background: '#3b82f6',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
          },
        },
        containedSecondary: {
          background: '#f97316',
          color: '#ffffff',
          '&:hover': {
            background: '#fb923c',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
            background: 'rgba(255, 255, 255, 0.02)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.15)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: '#111111',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            background: 'rgba(255, 255, 255, 0.03)',
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2563eb',
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'rgba(10, 10, 10, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          '&.MuiCircularProgress-colorPrimary': {
            color: '#2563eb',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 500,
        },
        standardSuccess: {
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          color: '#34d399',
        },
        standardError: {
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#f87171',
        },
        standardWarning: {
          background: 'rgba(245, 158, 11, 0.1)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          color: '#fbbf24',
        },
        standardInfo: {
          background: 'rgba(37, 99, 235, 0.1)',
          border: '1px solid rgba(37, 99, 235, 0.2)',
          color: '#60a5fa',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (max-width: 768px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
  },
});

// Custom theme extensions for premium effects
premiumTheme.customEffects = {
  gradients: {
    primary: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    secondary: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    accent: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    glass: 'linear-gradient(180deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
    nav: 'rgba(10, 10, 10, 0.8)',
  },
  animations: {
    float: 'float 5s ease-in-out infinite',
  },
  glass: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(24px)',
  },
  shadows: {
    glow: '0 0 40px rgba(37, 99, 235, 0.1)',
    premium: '0 24px 48px rgba(0, 0, 0, 0.5)',
  },
};

export default premiumTheme;